import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Eraser, Check } from "lucide-react";

// ── Drawing Player — rendered for both drawer and guessers ───────────────────
// Props:
//   isDrawer     : bool
//   word         : string   — only meaningful when isDrawer
//   hint         : string   — only meaningful when !isDrawer
//   roomId       : string
//   socket       : Socket
//   guess        : string
//   setGuess     : (v) => void
//   onGuess      : () => void
//   canGuess     : bool
//   revealedWord : string | null

export default function DrawingPlayer({ isDrawer, word, hint, roomId, socket, guess, setGuess, onGuess, canGuess, revealedWord }) {
  return isDrawer
    ? <DrawerController word={word} roomId={roomId} socket={socket} revealedWord={revealedWord} />
    : <GuesserController hint={hint} guess={guess} setGuess={setGuess} onGuess={onGuess} canGuess={canGuess} revealedWord={revealedWord} />;
}

// ── Drawer ────────────────────────────────────────────────────────────────────
function DrawerController({ word, roomId, socket, revealedWord }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState("#FFFFFF");
  const [brushSize, setBrushSize] = useState(5);
  const [erasing, setErasing] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const lastEmitRef = useRef(0);

  const colors = [
    "#FFFFFF", "#C0C0C0", "#808080", "#000000",
    "#FF0055", "#FF6600", "#FFAA00", "#FFFF00",
    "#00FF00", "#00FF99", "#00FFFF", "#00AAFF",
    "#0055FF", "#AA00FF", "#FF00FF", "#FF0099",
    "#8B4513", "#228B22", "#FF4500", "#FFD700",
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0d0020";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPosRef.current = getPos(e);
  }, [getPos]);

  const emitSnapshot = useCallback((force = false) => {
    const canvas = canvasRef.current;
    if (!canvas || !socket || !socket.connected) return;
    const now = Date.now();
    // A full 400x300 JPEG on every pointer event can flood the room.
    if (!force && now - lastEmitRef.current < 100) return;
    lastEmitRef.current = now;
    socket.emit("drawing_data", { room_id: roomId, drawing: canvas.toDataURL("image/jpeg", 0.5) });
  }, [socket, roomId]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    emitSnapshot(true);
  }, [emitSnapshot]);

  const drawImpl = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = erasing ? "#0d0020" : color;
    ctx.lineWidth = erasing ? brushSize * 3 : brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPosRef.current = pos;
    emitSnapshot(false);
  }, [isDrawing, color, brushSize, erasing, getPos, emitSnapshot]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0d0020";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    emitSnapshot(true);
  };

  const giveUp = () => {
    if (socket) socket.emit("end_drawing_turn", { room_id: roomId });
  };

  return (
    <div className="flex-1 flex flex-col gap-2">
      <div className="text-center">
        <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Рисуй слово:</p>
        <h2 className="font-heading text-2xl text-[#FFFF00] neon-yellow">{word}</h2>
      </div>

      {revealedWord && (
        <div className="text-center animate-bounce-in">
          <p className="font-heading text-xl text-[#39FF14]">Раунд окончен!</p>
        </div>
      )}

      {/* Canvas */}
      <div className="relative" style={{ borderRadius: "0.75rem", overflow: "hidden", border: "2px solid #444", background: "#0d0020" }}>
        <canvas
          ref={canvasRef}
          width={400}
          height={300}
          data-testid="drawing-canvas"
          className="w-full"
          style={{ touchAction: "none", display: "block", cursor: erasing ? "cell" : "crosshair" }}
          onMouseDown={startDrawing}
          onMouseMove={drawImpl}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={drawImpl}
          onTouchEnd={stopDrawing}
        />
      </div>

      {/* Color palette */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: "5px" }}>
        {colors.map((c) => (
          <button
            key={c}
            onClick={() => { setColor(c); setErasing(false); }}
            data-testid={`color-${c}`}
            style={{
              backgroundColor: c, width: "100%", aspectRatio: "1", borderRadius: "50%",
              border: (!erasing && color === c) ? "3px solid #fff" : "2px solid #333",
              transform: (!erasing && color === c) ? "scale(1.2)" : "scale(1)",
              transition: "transform 0.1s",
            }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        <input
          type="range" min="2" max="24" value={brushSize}
          onChange={(e) => setBrushSize(Number(e.target.value))}
          className="flex-1"
        />
        <span className="text-[#aaa] text-xs" style={{ minWidth: "1.5rem", textAlign: "center" }}>{brushSize}</span>
        <button
          onClick={() => setErasing(e => !e)}
          className={"btn-cartoon px-3 py-2 " + (erasing ? "btn-primary" : "btn-secondary")}
          title="Ластик"
        >
          <Eraser className="w-4 h-4" />
        </button>
        <button onClick={clearCanvas} data-testid="clear-canvas-btn" className="btn-cartoon btn-error px-3 py-2" title="Очистить">
          🗑️
        </button>
      </div>

      {!revealedWord && (
        <button onClick={giveUp} className="btn-cartoon btn-secondary w-full py-2" style={{ opacity: 0.8 }}>
          🏳️ Сдаюсь — пропустить слово
        </button>
      )}
    </div>
  );
}

// ── Guesser ───────────────────────────────────────────────────────────────────
function GuesserController({ hint, guess, setGuess, onGuess, canGuess, revealedWord }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onGuess();
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="text-center mb-6">
        <p className="text-[#A1A1AA] text-sm">ПОДСКАЗКА:</p>
        <h2 className="font-heading text-xl text-[#00FFFF]">{hint}</h2>
      </div>

      {revealedWord ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center animate-bounce-in">
            <p className="text-[#A1A1AA] mb-2">Это было:</p>
            <p className="font-heading text-3xl text-[#FFFF00] neon-yellow">{revealedWord}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="text-center mb-6">
            {canGuess ? (
              <p className="text-[#A1A1AA]">Смотри на экран и угадывай!</p>
            ) : (
              <div className="animate-bounce-in">
                <Check className="w-12 h-12 text-[#39FF14] mx-auto mb-2" />
                <p className="text-[#39FF14] font-bold">Ты угадал!</p>
              </div>
            )}
          </div>
          <div className="mt-auto">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="text"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Твой ответ..."
                disabled={!canGuess}
                data-testid="guess-input"
                className="input-cartoon flex-1 text-lg py-4 normal-case tracking-normal"
              />
              <button
                type="submit"
                disabled={!canGuess || !guess.trim()}
                data-testid="guess-submit-btn"
                className={`btn-neon px-6 ${canGuess && guess.trim() ? "btn-success" : "bg-[#2D2D3A] text-[#A1A1AA]"}`}
              >
                <Send className="w-6 h-6" />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
